import React, {useEffect, useRef, useState} from 'react';
import {Announcement} from '../../types/announcement';
import {announcementService} from '../../services/announcementService';
import './AnnouncementBanner.css';

interface AnnouncementBannerProps {
    onAnnouncementClick: (announcement: Announcement) => void;
}

const AnnouncementBanner: React.FC<AnnouncementBannerProps> = ({onAnnouncementClick}) => {
    const [announcements, setAnnouncements] = useState<Announcement[]>([]);
    const [needsScroll, setNeedsScroll] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        announcementService.getActiveAnnouncements()
            .then(setAnnouncements)
            .catch(() => {
                setAnnouncements([]);
            });
    }, []);

    // 检查内容宽度是否超过容器宽度
    useEffect(() => {
        if (announcements.length <= 1) return;

        const checkScroll = () => {
            const container = containerRef.current;
            const content = contentRef.current;
            if (container && content) {
                // 比较内容实际宽度和容器宽度
                setNeedsScroll(content.scrollWidth > container.clientWidth);
            }
        };

        // 等待内容渲染后检查
        const timer = setTimeout(checkScroll, 100);
        window.addEventListener('resize', checkScroll);

        return () => {
            clearTimeout(timer);
            window.removeEventListener('resize', checkScroll);
        };
    }, [announcements]);

    // 无公告时不渲染
    if (announcements.length === 0) {
        return null;
    }

    // 只有一个公告，居中展示
    if (announcements.length === 1) {
        return (
            <div
                className="announcement-banner"
                style={{
                    background: 'linear-gradient(90deg, rgba(240, 180, 41, 0.1) 0%, rgba(240, 180, 41, 0.05) 100%)',
                    padding: '4px 8px',
                    marginBottom: 16,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: 44,
                }}
            >
                <span
                    className="announcement-title"
                    style={{
                        color: 'rgba(255, 255, 255, 0.9)',
                        fontSize: 14,
                        cursor: 'pointer',
                        padding: '4px 8px',
                    }}
                    onClick={() => onAnnouncementClick(announcements[0])}
                >
                    📢 {announcements[0].title}
                </span>
            </div>
        );
    }

    // 多个公告 - 根据内容宽度决定是否滚动
    return (
        <div
            ref={containerRef}
            className="announcement-banner"
            style={{
                background: 'linear-gradient(90deg, rgba(240, 180, 41, 0.1) 0%, rgba(240, 180, 41, 0.05) 100%)',
                padding: '4px 8px',
                marginBottom: 16,
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                minHeight: 44,
                overflow: 'hidden',
            }}
        >
            <div className="announcement-label" style={{flexShrink: 0}}>
                <span className="announcement-icon">📢</span>
                <span>公告</span>
            </div>
            <div
                ref={contentRef}
                className={needsScroll ? 'announcement-marquee-container' : ''}
                style={{
                    flex: 1,
                    overflow: needsScroll ? 'hidden' : 'visible',
                }}
            >
                <div
                    className={needsScroll ? 'announcement-marquee-content' : ''}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: needsScroll ? 'flex-start' : 'center',
                        whiteSpace: needsScroll ? 'nowrap' : 'normal',
                    }}
                >
                    {announcements.map((announcement, index) => (
                        <span key={announcement.id || index}>
                            <span
                                className="announcement-title"
                                style={{
                                    color: 'rgba(255, 255, 255, 0.9)',
                                    fontSize: 14,
                                    cursor: 'pointer',
                                    padding: '4px 8px',
                                }}
                                onClick={() => onAnnouncementClick(announcement)}
                            >
                                {announcement.title}
                            </span>
                            {index < announcements.length - 1 && (
                                <span className="announcement-separator">  •  </span>
                            )}
                        </span>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default AnnouncementBanner;
